import { defineStore } from 'pinia'
import type {
  ITextList,
  ITextListItem,
} from '@team-milva/lab-models/resources'
import { SYSTEM_TEXT_LIST_KEYS } from '@team-milva/lab-models/resources'
import {
  collection,
  doc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/firebase/init'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAuthStore } from '@/stores/authStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import type { TLibraryVisibilityFilter } from '@/globals/typescript/utils/libraryVisibilityFilter'

export const useTextListStore = defineStore('textListStore', {
  state: () => ({
    allTextLists: [] as ITextList[],
    unsubscribeTextLists: null as (() => void) | null,
    labelsLibrarySearchText: '',
    resultsVisibilityFilter: 'active' as TLibraryVisibilityFilter,
    unitsVisibilityFilter: 'active' as TLibraryVisibilityFilter,
  }),

  getters: {
    allTextListsLoaded: (state): boolean => !!state.unsubscribeTextLists,

    systemLists: (state): ITextList[] => {
      return SYSTEM_TEXT_LIST_KEYS.map((key) => {
        const found = state.allTextLists.find(
          (l) => l.type === 'system' && (l as ITextList).systemKey === key
        )
        if (found) return found as ITextList
        return {
          id: key,
          type: 'system',
          systemKey: key,
          labAccountID: '',
          items: {},
          timestamps: { createdAt: 0, updatedAt: 0 },
        } satisfies ITextList
      })
    },

  },

  actions: {
    getTextLists(): Promise<ITextList[]> {
      if (this.allTextListsLoaded) return Promise.resolve(this.allTextLists)

      return new Promise((resolve) => {
        let resolved = false
        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'TextLists'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (snapshot) => {
          // Filtered, not just cast: legacy `type: 'custom'` documents survive
          // in Firestore until this plan's v1.0.17 migration runs, and this
          // deploy lands first. Dropping them here keeps the gap invisible.
          this.allTextLists = snapshot.docs
            .map((d) => d.data() as ITextList)
            .filter((l) => l.type === 'system')
          this.unsubscribeTextLists = unsubscribe
          if (!resolved) {
            resolved = true
            resolve(this.allTextLists)
          }
        })
      })
    },

    async addTextListItem(listID: string, itemText: string, itemID?: string): Promise<string> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()
      const authStore = useAuthStore()

      const newItemID = itemID ?? doc(collection(db, 'x')).id
      const item: ITextListItem = {
        text: { [currentLocale]: itemText },
        activeState: 'active',
        createdAt: Date.now(),
        createdByUserID: authStore.user?.uid ?? '',
      }

      const isSystemKey = (SYSTEM_TEXT_LIST_KEYS as string[]).includes(listID)
      // Callers pass either a systemKey ('result-texts') or a real doc id.
      const existingDoc = this.allTextLists.find(
        (l) => l.id === listID || l.systemKey === listID
      )

      if (!existingDoc && isSystemKey) {
        const docRef = doc(
          collection(db, 'Apps', '-lab-management-', 'TextLists')
        )
        const data: ITextList = {
          id: docRef.id,
          type: 'system',
          systemKey: listID as (typeof SYSTEM_TEXT_LIST_KEYS)[number],
          labAccountID: accountStore.labAccount?.id ?? '',
          items: { [newItemID]: item },
          timestamps: { createdAt: Date.now(), updatedAt: Date.now() },
        }
        await setDoc(docRef, data)
        return newItemID
      }

      const docID = existingDoc?.id ?? listID
      const docRef = doc(db, 'Apps', '-lab-management-', 'TextLists', docID)
      await updateDoc(docRef, {
        [`items.${newItemID}`]: item,
        'timestamps.updatedAt': Date.now(),
      })
      return newItemID
    },

    async updateTextListItemText(
      listID: string,
      itemID: string,
      text: string
    ): Promise<void> {
      const { currentLocale } = useAppStore()
      const docID =
        this.allTextLists.find((l) => l.systemKey === listID)?.id ?? listID
      const docRef = doc(db, 'Apps', '-lab-management-', 'TextLists', docID)
      await updateDoc(docRef, {
        [`items.${itemID}.text.${currentLocale}`]: text,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async setTextListItemActive(
      listID: string,
      itemID: string,
      activeState: 'active' | 'inactive' | 'archived'
    ): Promise<void> {
      const accountStore = useAccountStore()
      const docID =
        this.allTextLists.find((l) => l.systemKey === listID)?.id ?? listID

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/text-lists/{textListID}/items/{textListItemID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            textListID: docID,
            textListItemID: itemID,
          },
          body: { activeState },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    setLabelsLibrarySearchText(text: string): void {
      this.labelsLibrarySearchText = text
    },

    resetStore(): void {
      this.allTextLists = []
      if (this.unsubscribeTextLists) {
        this.unsubscribeTextLists()
        this.unsubscribeTextLists = null
      }
      this.resultsVisibilityFilter = 'active'
      this.unitsVisibilityFilter = 'active'
      this.labelsLibrarySearchText = ''
    },
  },
})
